/**
 * Köttbutiken: annonsraden (sections/kb-annonsrad.liquid).
 * Roterande läge: byter meddelande efter vald tid, pausar vid hover och när fliken är dold.
 * Rullande läge: klonar meddelandegruppen så att bandet täcker bredden och räknar ut
 * animationstiden från hastigheten (px/s), så att farten är densamma oavsett textlängd.
 */
class KbAnnonsrad extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #aktuell = 0;
  /** @type {ResizeObserver | undefined} */
  #storlek;

  connectedCallback() {
    if (this.dataset.lage === 'rotera') this.#startaRotation();
    if (this.dataset.lage === 'rulla') this.#startaBand();

    document.addEventListener('shopify:block:select', this.#vidBlockval);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#storlek?.disconnect();
    document.removeEventListener('visibilitychange', this.#vidSynlighet);
    document.removeEventListener('shopify:block:select', this.#vidBlockval);
  }

  /* ---------- Roterande ---------- */

  get #meddelanden() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-kb-grupp] > [data-kb-annons]')]);
  }

  #startaRotation() {
    if (this.#meddelanden.length < 2) return;

    this.querySelector('[data-kb-foregaende]')?.addEventListener('click', () => this.#visa(this.#aktuell - 1, true));
    this.querySelector('[data-kb-nasta]')?.addEventListener('click', () => this.#visa(this.#aktuell + 1, true));
    document.addEventListener('visibilitychange', this.#vidSynlighet);
    this.#spela();
  }

  #spela() {
    clearInterval(this.#timer);
    const tid = (Number(this.dataset.tid) || 5) * 1000;
    this.#timer = window.setInterval(() => {
      if (document.hidden) return;
      if (this.hasAttribute('data-pausa') && this.matches(':hover')) return;
      this.#visa(this.#aktuell + 1);
    }, tid);
  }

  /**
   * @param {number} index
   * @param {boolean} [manuellt] - startar om timern så att meddelandet får sin fulla tid
   */
  #visa(index, manuellt = false) {
    const lista = this.#meddelanden;
    if (!lista.length) return;

    const nytt = ((index % lista.length) + lista.length) % lista.length;
    if (nytt === this.#aktuell) return;

    const gammalt = lista[this.#aktuell];
    if (gammalt) {
      gammalt.setAttribute('data-ut', '');
      window.setTimeout(() => gammalt.removeAttribute('data-ut'), 550);
    }

    lista.forEach((meddelande, i) => meddelande.setAttribute('aria-hidden', String(i !== nytt)));
    this.#aktuell = nytt;
    if (manuellt) this.#spela();
  }

  #vidSynlighet = () => {
    if (!document.hidden) this.#spela();
  };

  /** I temaredigeraren: visa meddelandet som markeras. */
  #vidBlockval = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const index = this.#meddelanden.indexOf(/** @type {HTMLElement} */ (event.target.closest('[data-kb-annons]')));
    if (index < 0) return;
    if (this.dataset.lage === 'rotera') {
      this.#visa(index);
      clearInterval(this.#timer);
    }
  };

  /* ---------- Rullande ---------- */

  #startaBand() {
    const spar = /** @type {HTMLElement | null} */ (this.querySelector('[data-kb-spar]'));
    const original = /** @type {HTMLElement | null} */ (this.querySelector('[data-kb-grupp]'));
    if (!spar || !original) return;

    const bygg = () => {
      spar.querySelectorAll('[data-kb-klon]').forEach((klon) => klon.remove());

      const gruppbredd = original.getBoundingClientRect().width;
      if (!gruppbredd) return;

      // Tillräckligt många kopior för att täcka bredden plus en grupp som glider in.
      const kopior = Math.ceil(spar.clientWidth / gruppbredd) + 1;
      for (let i = 0; i < kopior; i++) {
        const klon = /** @type {HTMLElement} */ (original.cloneNode(true));
        klon.setAttribute('data-kb-klon', '');
        klon.setAttribute('aria-hidden', 'true');
        klon.querySelectorAll('a').forEach((lank) => lank.setAttribute('tabindex', '-1'));
        klon.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        spar.append(klon);
      }

      const hastighet = Number(this.dataset.hastighet) || 40;
      spar.style.setProperty('--kb-rull-tid', `${gruppbredd / hastighet}s`);
      spar.style.setProperty('--kb-rull-riktning', this.dataset.riktning === 'hoger' ? 'reverse' : 'normal');
      spar.style.setProperty('--kb-rull-paus', 'running');
    };

    // Vänta på typsnittet så att bredden mäts rätt.
    document.fonts?.ready.then(bygg) ?? bygg();

    let senasteBredd = spar.clientWidth;
    this.#storlek = new ResizeObserver(() => {
      if (spar.clientWidth === senasteBredd) return;
      senasteBredd = spar.clientWidth;
      bygg();
    });
    this.#storlek.observe(spar);
  }
}

if (!customElements.get('kb-annonsrad')) {
  customElements.define('kb-annonsrad', KbAnnonsrad);
}
